import Modal from '../ui/Modal';
import { ArrowRight, Plus, Minus, Edit } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface VersionComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  comparison: {
    version1: {
      versionNumber: number;
      createdAt: string;
      createdBy?: string;
      changeType: string;
    };
    version2: {
      versionNumber: number;
      createdAt: string;
      createdBy?: string;
      changeType: string;
    };
    changes: any;
  } | null;
  isLoading?: boolean;
}

export function VersionComparisonModal({
  isOpen,
  onClose,
  comparison,
  isLoading,
}: VersionComparisonModalProps) {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Version Comparison" size="xl">
      {isLoading ? (
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading comparison...</p>
        </div>
      ) : comparison ? (
        <div className="p-6">
          {/* Header */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Version</p>
              <p className="text-2xl font-bold text-gray-900">{comparison.version1.versionNumber}</p>
              <p className="text-xs text-gray-500 mt-2">
                {formatDistanceToNow(new Date(comparison.version1.createdAt), { addSuffix: true })}
              </p>
              {comparison.version1.createdBy && (
                <p className="text-xs text-gray-500">by {comparison.version1.createdBy}</p>
              )}
            </div>

            <div className="flex items-center justify-center">
              <ArrowRight className="w-8 h-8 text-gray-400" />
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 mb-1">Version</p>
              <p className="text-2xl font-bold text-blue-900">{comparison.version2.versionNumber}</p>
              <p className="text-xs text-blue-600 mt-2">
                {formatDistanceToNow(new Date(comparison.version2.createdAt), { addSuffix: true })}
              </p>
              {comparison.version2.createdBy && (
                <p className="text-xs text-blue-600">by {comparison.version2.createdBy}</p>
              )}
            </div>
          </div>

          {/* Changes */}
          <div className="space-y-6">
            {/* Schedule/Forecast field changes */}
            {(comparison.changes.schedule || comparison.changes.forecast) && (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Field Changes</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {Object.entries(comparison.changes.schedule || comparison.changes.forecast || {}).map(
                    ([field, change]: [string, any]) => (
                      <div key={field} className="border-b border-gray-200 last:border-0 pb-3 last:pb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Edit className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900 capitalize">
                            {field.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 ml-6">
                          <div className="col-span-1">
                            <p className="text-xs text-gray-500 mb-1">Old Value</p>
                            <div className="bg-red-50 rounded p-2">
                              <code className="text-xs text-red-900 break-all">
                                {typeof change.old === 'object'
                                  ? JSON.stringify(change.old, null, 2)
                                  : String(change.old)}
                              </code>
                            </div>
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="col-span-1">
                            <p className="text-xs text-blue-600 mb-1">New Value</p>
                            <div className="bg-blue-50 rounded p-2">
                              <code className="text-xs text-blue-900 break-all">
                                {typeof change.new === 'object'
                                  ? JSON.stringify(change.new, null, 2)
                                  : String(change.new)}
                              </code>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Shift changes */}
            {comparison.changes.shifts && (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Shift Changes</h4>
                <div className="space-y-3">
                  {comparison.changes.shifts.added.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Plus className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-900">
                          {comparison.changes.shifts.added.length} Shift(s) Added
                        </span>
                      </div>
                      <div className="space-y-2 ml-7">
                        {comparison.changes.shifts.added.slice(0, 5).map((shift: any, idx: number) => (
                          <div key={idx} className="text-sm text-green-800">
                            • {shift.agentId} | {new Date(shift.startTime).toLocaleString()} -{' '}
                            {new Date(shift.endTime).toLocaleString()}
                          </div>
                        ))}
                        {comparison.changes.shifts.added.length > 5 && (
                          <p className="text-sm text-green-600">
                            and {comparison.changes.shifts.added.length - 5} more...
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {comparison.changes.shifts.removed.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Minus className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-900">
                          {comparison.changes.shifts.removed.length} Shift(s) Removed
                        </span>
                      </div>
                      <div className="space-y-2 ml-7">
                        {comparison.changes.shifts.removed.slice(0, 5).map((shift: any, idx: number) => (
                          <div key={idx} className="text-sm text-red-800">
                            • {shift.agentId} | {new Date(shift.startTime).toLocaleString()} -{' '}
                            {new Date(shift.endTime).toLocaleString()}
                          </div>
                        ))}
                        {comparison.changes.shifts.removed.length > 5 && (
                          <p className="text-sm text-red-600">
                            and {comparison.changes.shifts.removed.length - 5} more...
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {comparison.changes.shifts.modified.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Edit className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">
                          {comparison.changes.shifts.modified.length} Shift(s) Modified
                        </span>
                      </div>
                      <div className="space-y-2 ml-7">
                        {comparison.changes.shifts.modified.slice(0, 5).map((change: any, idx: number) => (
                          <div key={idx} className="text-sm text-blue-800">
                            • Shift {change.id}: {Object.keys(change.changes).join(', ')} changed
                          </div>
                        ))}
                        {comparison.changes.shifts.modified.length > 5 && (
                          <p className="text-sm text-blue-600">
                            and {comparison.changes.shifts.modified.length - 5} more...
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {comparison.changes.shifts.added.length === 0 &&
                    comparison.changes.shifts.removed.length === 0 &&
                    comparison.changes.shifts.modified.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No shift changes</p>
                    )}
                </div>
              </div>
            )}

            {/* Data point changes (for forecasts) */}
            {comparison.changes.dataPoints && (
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Data Point Changes</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Data Points</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {comparison.changes.dataPoints.totalOld}{' '}
                        <ArrowRight className="inline w-4 h-4 text-gray-400" />{' '}
                        {comparison.changes.dataPoints.totalNew}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {comparison.changes.dataPoints.added > 0 && (
                      <div className="bg-green-100 rounded p-3 text-center">
                        <Plus className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-900">
                          {comparison.changes.dataPoints.added}
                        </p>
                        <p className="text-xs text-green-700">Added</p>
                      </div>
                    )}
                    {comparison.changes.dataPoints.removed > 0 && (
                      <div className="bg-red-100 rounded p-3 text-center">
                        <Minus className="w-5 h-5 text-red-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-red-900">
                          {comparison.changes.dataPoints.removed}
                        </p>
                        <p className="text-xs text-red-700">Removed</p>
                      </div>
                    )}
                    {comparison.changes.dataPoints.modified > 0 && (
                      <div className="bg-blue-100 rounded p-3 text-center">
                        <Edit className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-blue-900">
                          {comparison.changes.dataPoints.modified}
                        </p>
                        <p className="text-xs text-blue-700">Modified</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-gray-600">No comparison data available</p>
        </div>
      )}
    </Modal>
  );
}
